import { GetLoggedUserUseCase } from "@authentication/domain/usecases/get-logged-user.usecase";
import { Pagination } from "@core/domain/models/pagination";
import { Nullable } from "@core/domain/types/nullable.type";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import {
    EntityApiKey,
    EntityApiKeys,
} from "@entity-api-keys/domain/models/entity-api-key.model";
import { DeleteEntityApiKeyUseCase } from "@entity-api-keys/domain/usecases/delete-entity-api-key.usecase";
import {
    SearchEntityApiKeysParams,
    SearchEntityApiKeysUseCase,
} from "@entity-api-keys/domain/usecases/search-entity-api-keys.usecase";
import { GlobalEntity } from "@entity/domain/models/global-entity.model";
import { GetAllGlobalEntitiesUseCase } from "@entity/domain/usecases/get-all-global-entities.usecase";
import { inject, injectable } from "inversify";
import { DateTime } from "luxon";
import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from "mobx";

export interface EntityApiKeyListTableRow {
    id: number;
    entityName: string;
    name: string;
    prefix: string;
    scopesCount: number;
    isActive: boolean;
    created: string;
}

export interface EntityApiScopeOption {
    value: string;
    label: string;
}

const scopeValues: string[] = [
    "employees:read",
    "employees:write",
    "documents:read",
    "entity:read",
    "entity:write",
    "entity_addresses:read",
    "entity_addresses:write",
    "entity_contacts:read",
    "entity_contacts:write",
    "entity_affiliations:read",
    "entity_affiliations:write",
    "creditors:read",
    "creditors:write",
    "suppliers:read",
    "suppliers:write",
    "materials:read",
    "materials:write",
    "revenues:read",
    "revenues:write",
    "costs:read",
    "costs:write",
    "entity_social_networks:read",
    "entity_social_networks:write",
    "entity_locals:read",
    "entity_locals:write",
    "entity_rooms:read",
    "entity_rooms:write",
    "entity_management_bodies:read",
    "entity_management_bodies:write",
    "entity_partners:read",
    "entity_partners:write",
    "entity_quality_certificates:read",
    "entity_quality_certificates:write",
    "entity_policy_protocols:read",
    "entity_policy_protocols:write",
    "entity_project_auditories:read",
    "entity_project_auditories:write",
    "entity_entity_auditories:read",
    "entity_entity_auditories:write",
    "beneficiaries:read",
    "beneficiaries:write",
    "services:read",
    "services:write",
    "derivations:read",
    "derivations:write",
    "projects:read",
    "projects:write",
];

interface EntityApiKeysListTable {
    apiKeys: EntityApiKeyListTableRow[];
    count: number;
}

@injectable()
export class EntityApiKeysListPageViewModel extends BaseViewModel {
    @observable
    initialLoading: boolean = true;

    @observable
    pagination: Pagination = new Pagination();

    @observable
    globalEntities: GlobalEntity[] = [];

    readonly scopeOptions: EntityApiScopeOption[] = scopeValues.map(
        (value) => ({
            value,
            label: value,
        }),
    );

    @observable
    filters: {
        search: string;
        entityId: Nullable<number>;
        createdFromDate: Nullable<DateTime>;
        createdToDate: Nullable<DateTime>;
        isActive: Nullable<boolean>;
        scopes: string[];
    } = {
        search: "",
        entityId: null,
        createdFromDate: null,
        createdToDate: null,
        isActive: null,
        scopes: [],
    };

    @observable
    private _keys: EntityApiKeys = new EntityApiKeys([], 0);

    constructor(
        @inject(SearchEntityApiKeysUseCase)
        private readonly searchUseCase: SearchEntityApiKeysUseCase,
        @inject(DeleteEntityApiKeyUseCase)
        private readonly deleteUseCase: DeleteEntityApiKeyUseCase,
        @inject(GetLoggedUserUseCase)
        private readonly getLoggedUserUseCase: GetLoggedUserUseCase,
        @inject(GetAllGlobalEntitiesUseCase)
        private readonly getAllGlobalEntitiesUseCase: GetAllGlobalEntitiesUseCase,
    ) {
        super();
        makeObservable(this);
    }

    private get isAdminUser(): boolean {
        const user = this.getLoggedUserUseCase.execute();
        return user.isSuperAdmin() || user.isAdminRole();
    }

    @computed
    get table(): EntityApiKeysListTable {
        const entitiesById = new Map(
            this.globalEntities.map((entity) => [entity.id, entity.name]),
        );

        return {
            count: this._keys.count,
            apiKeys: this._keys.apiKeys.map((key: EntityApiKey) => ({
                id: key.id,
                entityName: entitiesById.get(key.entityId) ?? "",
                name: key.name,
                prefix: key.prefix,
                scopesCount: key.scopes.length,
                isActive: key.isActive,
                created: key.created.toLocaleString(DateTime.DATETIME_SHORT),
            })),
        };
    }

    override async didMount(): Promise<void> {
        await super.didMount();

        if (this.isAdminUser) {
            const globalEntities =
                await this.getAllGlobalEntitiesUseCase.execute();
            runInAction(() => {
                this.globalEntities = globalEntities;
            });
        }

        await this.refresh();
        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    setFilters(filters: {
        search: string;
        entityId: Nullable<number>;
        createdFromDate: Nullable<DateTime>;
        createdToDate: Nullable<DateTime>;
        isActive: Nullable<boolean>;
        scopes: string[];
    }): void {
        this.filters = filters;
        this.pagination.reset();
        this.refresh();
    }

    @action
    setPagination(page: number, pageSize: number): void {
        this.pagination.page = page;
        this.pagination.pageSize = pageSize;
        this.refresh();
    }

    async refresh(): Promise<void> {
        const params: SearchEntityApiKeysParams = {};
        if (this.filters.search) params.search = this.filters.search;
        if (this.filters.entityId !== null)
            params.entityId = this.filters.entityId;
        const createdAfter = this.filters.createdFromDate?.toISODate() ?? null;
        if (createdAfter) params.createdAfter = createdAfter;
        const createdBefore = this.filters.createdToDate?.toISODate() ?? null;
        if (createdBefore) params.createdBefore = createdBefore;
        if (this.filters.isActive !== null)
            params.isActive = this.filters.isActive;
        if (this.filters.scopes.length > 0) params.scopes = this.filters.scopes;

        const keys = await this.searchUseCase.execute(this.pagination, params);
        runInAction(() => {
            this._keys = keys;
        });
    }

    async deleteKey(id: number): Promise<boolean> {
        const ok = await this.deleteUseCase.execute(id);
        if (ok) await this.refresh();
        return ok;
    }
}
